// MarkdownToolbar — barra de formatação markdown reutilizável.
//
// Atalhos: ⌘B (bold), ⌘I (italic), ⌘E (code inline).
// Click no botão wrappa a seleção atual OU insere placeholder.
// Cursor reposicionado automaticamente depois de inserir.
//
// Props:
//   textareaRef — RefObject<HTMLTextAreaElement|HTMLInputElement>
//   value       — string atual do input
//   onChange    — (next: string) => void
//   compact     — true esconde label "Markdown" e usa h-6 (composers pequenos)

import React, { useEffect } from 'react';
import { Bold, Code, Italic, Link2, List, ListOrdered, Quote } from 'lucide-react';

const ITEMS = [
  { id: 'bold',      Icon: Bold,        title: 'Negrito (⌘B)' },
  { id: 'italic',    Icon: Italic,      title: 'Itálico (⌘I)' },
  { id: 'code',      Icon: Code,        title: 'Código inline (⌘E)' },
  { id: 'codeblock', Icon: Code,        title: 'Bloco de código' },
  { id: 'quote',     Icon: Quote,       title: 'Citação' },
  { id: 'ul',        Icon: List,        title: 'Lista' },
  { id: 'ol',        Icon: ListOrdered, title: 'Lista numerada' },
  { id: 'link',      Icon: Link2,       title: 'Link' },
];

const DIVIDER_AFTER = new Set([2, 3, 6]); // posição dos dividers

export default function MarkdownToolbar({ textareaRef, value, onChange, compact = false }) {
  // Insere texto via `document.execCommand('insertText')` em vez de
  // recompor a string + `onChange`. Por que:
  //   • execCommand passa pelo input/text engine nativo do browser →
  //     a mudança ENTRA no undo stack (Cmd+Z desfaz a formatação).
  //   • Dispara `input` event nativamente → React onChange fica em sync.
  //   • Funciona em <textarea> E <input type="text">.
  // Tradeoff: API marcada deprecated mas suportada por todos os browsers
  // modernos (Chromium/WebKit/Firefox) e não há substituto que mantenha
  // o undo stack. Quando substituto vier, troca aqui.
  function applyFormat(format) {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const selected = value.slice(start, end);
    let before = '';
    let after = '';
    let placeholder = '';

    switch (format) {
      case 'bold':       before = '**'; after = '**'; placeholder = 'texto'; break;
      case 'italic':     before = '_';  after = '_';  placeholder = 'texto'; break;
      case 'code':       before = '`';  after = '`';  placeholder = 'código'; break;
      case 'codeblock':  before = '\n```\n'; after = '\n```\n'; placeholder = 'código aqui'; break;
      case 'quote':      before = '\n> '; after = ''; placeholder = 'citação'; break;
      case 'ul':         before = '\n- '; after = ''; placeholder = 'item'; break;
      case 'ol':         before = '\n1. '; after = ''; placeholder = 'item'; break;
      case 'link':       before = '[';  after = '](url)'; placeholder = 'texto do link'; break;
      default: return;
    }

    const insert = selected || placeholder;
    const replacement = before + insert + after;

    el.focus();
    el.setSelectionRange(start, end);

    // execCommand mantém undo stack nativo (Cmd+Z funciona).
    // Fallback p/ setRangeText quando execCommand bloqueado ou não disponível.
    let inserted = false;
    try {
      inserted = document.execCommand('insertText', false, replacement);
    } catch {
      inserted = false;
    }
    if (!inserted) {
      // Fallback: setRangeText + dispatch synthetic input event pra notificar
      // React. NÃO entra no undo stack do browser (limitação conhecida) —
      // mas pelo menos a inserção funciona.
      if (typeof el.setRangeText === 'function') {
        el.setRangeText(replacement, start, end, 'end');
        el.dispatchEvent(new Event('input', { bubbles: true }));
      } else {
        const next = value.slice(0, start) + replacement + value.slice(end);
        onChange(next);
      }
    }

    // Reposiciona seleção pro placeholder (caso não havia seleção original).
    setTimeout(() => {
      el.focus();
      if (selected) {
        const pos = start + replacement.length;
        el.setSelectionRange(pos, pos);
      } else {
        const a = start + before.length;
        el.setSelectionRange(a, a + placeholder.length);
      }
    }, 0);
  }

  // Keyboard shortcuts diretamente no textarea ref.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return undefined;
    function onKey(e) {
      if (!(e.metaKey || e.ctrlKey)) return;
      const k = e.key.toLowerCase();
      if (k === 'b') { e.preventDefault(); applyFormat('bold'); }
      else if (k === 'i') { e.preventDefault(); applyFormat('italic'); }
      else if (k === 'e') { e.preventDefault(); applyFormat('code'); }
    }
    el.addEventListener('keydown', onKey);
    return () => el.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, textareaRef]);

  const buttonSize = compact ? 'h-6 w-6' : 'h-7 w-7';
  const iconSize = compact ? 11 : 12;

  return (
    <div
      role="toolbar"
      aria-label="Formatação markdown"
      className={`flex items-center gap-0.5 ${compact ? 'px-2 py-1' : 'px-3 py-1.5'}`}
    >
      {ITEMS.map((it, i) => (
        <React.Fragment key={it.id}>
          {DIVIDER_AFTER.has(i) && (
            <span className="mx-0.5 h-4 w-px bg-apolo-borderSubtle" aria-hidden="true" />
          )}
          <button
            type="button"
            onClick={() => applyFormat(it.id)}
            title={it.title}
            aria-label={it.title}
            className={`relative inline-flex ${buttonSize} items-center justify-center rounded text-apolo-textTertiary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing`}
          >
            <it.Icon size={iconSize} aria-hidden="true" />
            {it.id === 'codeblock' && (
              <span
                aria-hidden="true"
                className="absolute -bottom-px right-1 text-[6px] font-bold leading-none text-apolo-textTertiary"
              >
                ▮
              </span>
            )}
          </button>
        </React.Fragment>
      ))}
      {!compact && (
        <span className="ml-auto text-[9px] uppercase tracking-wider text-apolo-textTertiary">
          Markdown
        </span>
      )}
    </div>
  );
}
